// ENG-6915: conversation OUTCOME-failure category aggregation for the
// platform-admin Dashboard.
//
// Companion to eval-scores.ts and eval-failures.ts, but a different question
// from both:
//   - eval-scores.ts  -> "are conversations succeeding?" (0-100 trend)
//   - eval-failures.ts -> "which conversations could the host NOT score, and why?"
//   - this file        -> "of the conversations that DID fail (verdict=failure),
//                          WHY did they fail?" (a closed-set reason breakdown)
//
// Pure, DB-agnostic, browser-safe (no node:* imports) so both the Hono API and
// the Next.js webapp can share it, exactly like the other conversations helpers.
//
// MODEL:
//   - A row enters here only once it has been evaluated to a `failure` verdict
//     AND the host evaluator named an outcome category (eval_failure_category).
//   - Windowed on `last_message_at` (the conversation's own clock), matching the
//     eval-FAILURE breakdown's framing of "conversations in this window".
//   - No "gave up" / retry concept: these rows are evaluated and terminal.

import type { ConversationMetricsPeriod } from './metrics.js';

/**
 * Closed taxonomy of outcome failure reasons. SINGLE SOURCE OF TRUTH - the DB
 * CHECK (20260623000002), the API write-route validation, and the host
 * evaluator all agree with this list.
 *   unresolved          - could not resolve the user's request.
 *   incorrect           - gave a wrong / misleading answer.
 *   missing_integration - lacked a connected integration / external service.
 *   missing_skill       - lacked a skill / ability to perform the task.
 *   lacking_permission  - lacked permission / authorization to act.
 *   out_of_scope        - request outside the agent's remit.
 *   user_abandoned      - user dropped off before resolution.
 *   agent_unresponsive  - agent stopped responding / never replied.
 *   other               - failed for a reason outside the set above.
 */
export const CONVERSATION_FAILURE_CATEGORIES = [
  'unresolved',
  'incorrect',
  'missing_integration',
  'missing_skill',
  'lacking_permission',
  'out_of_scope',
  'user_abandoned',
  'agent_unresponsive',
  'other',
] as const;

export type ConversationFailureCategory = (typeof CONVERSATION_FAILURE_CATEGORIES)[number];

/**
 * Canonical label + description per category. SINGLE SOURCE OF TRUTH for the
 * human-facing copy: the dashboard reads `label` for axes/legends and
 * `description` for hover, and the host eval prompt is built from `description`
 * (buildFailureCategoryPromptLines) so what the model is told and what operators
 * see can never drift.
 */
export const CONVERSATION_FAILURE_CATEGORY_INFO: Record<
  ConversationFailureCategory,
  { label: string; description: string }
> = {
  unresolved: {
    label: 'Unresolved',
    description: "Could not resolve the user's request.",
  },
  incorrect: {
    label: 'Incorrect answer',
    description: 'Gave a wrong or misleading answer.',
  },
  missing_integration: {
    label: 'Missing integration',
    description: 'Lacked a connected integration or external service needed to help.',
  },
  missing_skill: {
    label: 'Missing skill',
    description: 'Lacked a skill or ability needed to perform the task.',
  },
  lacking_permission: {
    label: 'Lacking permission',
    description: 'Lacked permission or authorization to perform the action.',
  },
  out_of_scope: {
    label: 'Out of scope',
    description: "Request was outside the agent's remit.",
  },
  user_abandoned: {
    label: 'User abandoned',
    description: 'User dropped off before resolution.',
  },
  agent_unresponsive: {
    label: 'Agent unresponsive',
    description: 'Agent stopped responding or never replied to the user.',
  },
  other: {
    label: 'Other',
    description: 'Failed for a reason outside the set above.',
  },
};

/**
 * The category menu as prompt lines (`"key" (description)`), so the host eval
 * prompt is generated from the same source the UI reads — no hand-kept copy.
 */
export function buildFailureCategoryPromptLines(): string {
  return CONVERSATION_FAILURE_CATEGORIES.map(
    (c) => `"${c}" (${CONVERSATION_FAILURE_CATEGORY_INFO[c].description})`,
  ).join(', ');
}

/** Type guard for an untrusted wire value. */
export function isConversationFailureCategory(v: unknown): v is ConversationFailureCategory {
  return (
    typeof v === 'string' &&
    (CONVERSATION_FAILURE_CATEGORIES as readonly string[]).includes(v)
  );
}

/** Window length per period (mirrors conversation metrics; no MA needed). */
const PERIOD_WINDOW_MS: Record<ConversationMetricsPeriod, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

/**
 * One failed conversation carrying an outcome category. The caller filters to
 * `eval_failure_category IS NOT NULL` and fetches the display window.
 */
export interface ConversationFailureCategoryRow {
  eval_failure_category: string;
  last_message_at: string;
}

/** Org-wide count for one category. */
export interface FailureCategoryCount {
  category: ConversationFailureCategory;
  count: number;
}

export interface ConversationFailureCategoriesResult {
  period: ConversationMetricsPeriod;
  period_start: string;
  /** Total failed conversations carrying a category in the window. */
  total: number;
  /** Breakdown ordered by count desc (every category present, zero-filled). */
  byCategory: FailureCategoryCount[];
}

function zeroCategoryMap(): Record<ConversationFailureCategory, number> {
  return CONVERSATION_FAILURE_CATEGORIES.reduce(
    (acc, c) => {
      acc[c] = 0;
      return acc;
    },
    {} as Record<ConversationFailureCategory, number>,
  );
}

/**
 * Aggregate failed conversation rows into an org-wide category breakdown. `now`
 * is injectable for deterministic tests. Rows outside the display window, or
 * with an unknown category, are ignored defensively.
 *
 * ponytail: org-wide breakdown only (the chart the issue asked for). Add a
 * per-agent table here if/when the dashboard wants to drill in by agent.
 */
export function aggregateConversationFailureCategories(
  rows: ConversationFailureCategoryRow[],
  period: ConversationMetricsPeriod,
  now: Date = new Date(),
): ConversationFailureCategoriesResult {
  const windowMs = PERIOD_WINDOW_MS[period];
  const nowMs = now.getTime();
  const periodStart = new Date(nowMs - windowMs);
  const periodStartMs = periodStart.getTime();

  const counts = zeroCategoryMap();
  let total = 0;

  for (const row of rows) {
    if (!isConversationFailureCategory(row.eval_failure_category)) continue; // ignore unknown
    const ts = new Date(row.last_message_at).getTime();
    if (!Number.isFinite(ts) || ts < periodStartMs || ts > nowMs) continue;
    counts[row.eval_failure_category] += 1;
    total += 1;
  }

  const byCategory: FailureCategoryCount[] = CONVERSATION_FAILURE_CATEGORIES.map((category) => ({
    category,
    count: counts[category],
  })).sort((a, b) => b.count - a.count || a.category.localeCompare(b.category));

  return {
    period,
    period_start: periodStart.toISOString(),
    total,
    byCategory,
  };
}
